'use client'

import { useState, useEffect, useCallback, useTransition } from 'react'
import { useResponsive } from '@dustin-web/microsite-components/src/hooks'
import debounce from 'lodash/debounce'
import {
  Touchpoint,
  type GetAutocompleteE4Query,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { getAutocompleteAction } from '@dustin-web/microsite-data/src/actions/autocomplete-action'

export function useAutocompleteAction(searchPhrase: string) {
  const [data, setData] = useState<GetAutocompleteE4Query | undefined>(undefined)
  const [isPending, startTransition] = useTransition()
  const [error, setError] = useState<Error | null>(null)
  const { isSmallScreen } = useResponsive()

  const fetchAutocomplete = useCallback(
    async (phrase: string) => {
      if (!phrase || phrase.length < 2) {
        setData(undefined)
        return
      }

      setError(null)

      startTransition(async () => {
        try {
          const touchpoint = isSmallScreen ? Touchpoint.Mobile : Touchpoint.Desktop
          const result = await getAutocompleteAction(phrase, touchpoint)

          if ('error' in result) {
            setError(new Error(result.error.message))
            setData(undefined)
          } else {
            setData(result)
          }
        } catch (err) {
          console.error('Autocomplete error:', err)
          setError(err instanceof Error ? err : new Error('Autocomplete failed'))
          setData(undefined)
        }
      })
    },
    [isSmallScreen]
  )

  const debouncedFetchAutocomplete = useCallback(
    debounce((phrase: string) => fetchAutocomplete(phrase), 300),
    [fetchAutocomplete]
  )

  useEffect(() => {
    debouncedFetchAutocomplete(searchPhrase)
    return () => debouncedFetchAutocomplete.cancel()
  }, [searchPhrase, debouncedFetchAutocomplete])

  return {
    data,
    isLoading: isPending,
    error,
  }
}
